import React from "react";
import { CookieConsent } from "../CookieConsent/CookieConsent";
import * as MarketingView from "../../../../../domain/models/MarketingView";
import { PROPERTYBID_REGISTERED_OFFICE } from "../../../../../domain/constants";

type TFooterColumn = {
    heading: string;
    links: Array<{ text: string; url: string }>;
};

export type TMarketingChrome = Pick<MarketingView.T, "brand" | "navLinks" | "phoneText" | "phoneUrl">;

const MENU_SCRIPT = `(function(){
  var btn=document.getElementById('marketing-menu-button');
  var panel=document.getElementById('marketing-menu');
  if(!btn||!panel){return;}
  function set(open){
    btn.setAttribute('aria-expanded',open?'true':'false');
    panel.hidden=!open;
    document.body.style.overflow=open?'hidden':'';
  }
  btn.addEventListener('click',function(){set(btn.getAttribute('aria-expanded')!=='true');});
  panel.addEventListener('click',function(e){if(e.target.tagName==='A'){set(false);}});
  document.addEventListener('keydown',function(e){
    if(e.key==='Escape'&&btn.getAttribute('aria-expanded')==='true'){set(false);btn.focus();}
  });
})();`;

const isSailLegal = (view: TMarketingChrome): boolean => view.brand === "sail_legal";

export const MarketingNav = (props: { view: TMarketingChrome }): JSX.Element =>
    <header className={`marketingNav${isSailLegal(props.view) ? " marketingNav--sailLegal" : ""}`}>
        <div className="marketingNav__inner">
            <a className="marketingNav__logo" href="/">
                {/* Sail Legal has no horizontal wordmark. The 2021 logo is the real lockup
                    but exists only in white, so the nav goes dark on this brand to carry it.
                    Sized to its own 521:397 ratio. */}
                <img
                    src={isSailLegal(props.view)
                        ? "/static/img/SAIL_LEGAL_LOGO_2021_WHITE.svg"
                        : "/static/img/sail_homes_full_logo.svg"}
                    alt={isSailLegal(props.view) ? "Sail Legal" : "Sail Homes"}
                    width={isSailLegal(props.view) ? 92 : 176}
                    height={isSailLegal(props.view) ? 70 : 30}
                />
            </a>

            <nav className="marketingNav__links" aria-label="Main">
                {props.view.navLinks
                    .filter((link) => !link.button)
                    .map((link) =>
                        <a className="marketingNav__link" href={link.url} key={link.url}>{link.text}</a>,
                    )}
            </nav>

            <div className="marketingNav__actions">
                <a className="marketingNav__tel" href={props.view.phoneUrl}>{props.view.phoneText}</a>
                {props.view.navLinks
                    .filter((link) => link.button)
                    .map((link) =>
                        <a className="marketingNav__cta" href={link.url} key={link.url}>{link.text}</a>,
                    )}

                {/* Below 48rem there were NO navigation links at all — just a logo and a
                    phone number — so a phone visitor could not reach pricing, questions or
                    the properties without scrolling to the footer. This audience is mostly
                    on phones. */}
                <button
                    className="marketingNav__menuButton"
                    id="marketing-menu-button"
                    type="button"
                    aria-expanded={false}
                    aria-controls="marketing-menu"
                    aria-label="Menu"
                >
                    <span className="marketingNav__menuBars" aria-hidden="true" />
                    <span className="marketingNav__menuLabel">Menu</span>
                </button>
            </div>
        </div>

        <nav className="marketingNav__panel" id="marketing-menu" aria-label="Main" hidden={true}>
            {(isSailLegal(props.view) ? SAIL_LEGAL_FOOTER_COLUMNS : MOBILE_MENU_LINKS).map((section) =>
                <div className="marketingNav__panelGroup" key={section.heading}>
                    <p className="marketingNav__panelHeading">{section.heading}</p>
                    {section.links.map((link) =>
                        <a className="marketingNav__panelLink" href={link.url} key={link.url}>{link.text}</a>,
                    )}
                </div>,
            )}

            {props.view.navLinks
                .filter((link) => link.button)
                .map((link) =>
                    <a className="marketingNav__panelCta" href={link.url} key={link.url}>{link.text}</a>,
                )}
            <a className="marketingNav__panelPhone" href={props.view.phoneUrl}>
                Or call {props.view.phoneText}
            </a>
        </nav>

        <script dangerouslySetInnerHTML={{ __html: MENU_SCRIPT }} />
    </header>;

interface IPartnerLogo {
    name: string;
    logoUrl: string;
}

class PartnerLogo extends React.Component<IPartnerLogo, { failed: boolean }> {
    public state = { failed: false };

    private onRef = (img: HTMLImageElement | null): void => {
        if (img !== null && img.complete && img.naturalWidth === 0) {
            this.setState({ failed: true });
        }
    };

    public render(): JSX.Element {
        return this.state.failed
            ? <span className="marketingPartnerLockup__name">{this.props.name}</span>
            : <img
                className="marketingPartnerLockup__partnerLogo"
                src={this.props.logoUrl}
                alt={this.props.name}
                ref={this.onRef}
                onError={() => this.setState({ failed: true })}
            />;
    }
}

export const MarketingPartnerNav = (props: {
    view: TMarketingChrome;
    partner: { name: string; logoUrl: string | null };
}): JSX.Element =>
    <header className={`marketingNav marketingNav--partner${isSailLegal(props.view) ? " marketingNav--sailLegal" : ""}`}>
        <div className="marketingNav__inner">
            <div className="marketingPartnerLockup">
                {props.partner.logoUrl === null
                    ? <span className="marketingPartnerLockup__name">{props.partner.name}</span>
                    : <PartnerLogo name={props.partner.name} logoUrl={props.partner.logoUrl} />}

                <span className="marketingPartnerLockup__plus" aria-hidden="true">+</span>

                <img
                    className="marketingPartnerLockup__sailLogo"
                    src={isSailLegal(props.view)
                        ? "/static/img/SAIL_LEGAL_LOGO_2021_WHITE.svg"
                        : "/static/img/sail_homes_full_logo.svg"}
                    alt={isSailLegal(props.view) ? "Sail Legal" : "Sail Homes"}
                    width={isSailLegal(props.view) ? 92 : 176}
                    height={isSailLegal(props.view) ? 70 : 30}
                />
            </div>

            <a className="marketingNav__tel" href={props.view.phoneUrl}>{props.view.phoneText}</a>
        </div>
    </header>;

const MOBILE_MENU_LINKS: Array<TFooterColumn> = [
    {
        heading: "Selling",
        links: [
            { text: "Get a free valuation", url: "/valuation/your-details" },
            { text: "What it costs", url: "/pricing" },
            { text: "Questions", url: "/faq" },
            { text: "Renovate for Sale", url: "/renovate-for-sale" },
        ],
    },
    {
        heading: "Buying",
        links: [
            { text: "Properties for sale", url: "/listings" },
            { text: "Buying a probate property", url: "/buying" },
        ],
    },
    {
        heading: "About",
        links: [
            { text: "Contact us", url: "/contact" },
            { text: "Blog", url: "/blog" },
        ],
    },
];

const FOOTER_COLUMNS: Array<TFooterColumn> = [
    {
        heading: "Selling",
        links: [
            { text: "Get a valuation", url: "/valuation/your-details" },
            { text: "How it works", url: "/faq" },
            { text: "Pricing", url: "/pricing" },
        ],
    },
    {
        heading: "Buying",
        links: [
            { text: "Properties for sale", url: "/listings" },
            { text: "Buying a probate property", url: "/buying" },
            { text: "Book a viewing", url: "/listings" },
        ],
    },
    {
        heading: "Services",
        links: [
            { text: "Renovate for Sale", url: "/renovate-for-sale" },
            { text: "Renovation case studies", url: "/renovate-for-sale/case-studies" },
            { text: "Blog", url: "/blog" },
        ],
    },
    {
        heading: "About",
        links: [
            { text: "Questions", url: "/faq" },
            { text: "Contact us", url: "/contact" },
            { text: "Regulatory bodies", url: "/compliance/regulatory" },
        ],
    },
    {
        heading: "Legal",
        links: [
            { text: "Privacy policy", url: "/compliance/privacy-policy" },
            { text: "Terms & conditions", url: "/compliance/terms-and-conditions" },
            { text: "Complaints procedure", url: "/compliance/complaints-procedure" },
        ],
    },
];

const SAIL_LEGAL_FOOTER_COLUMNS: Array<TFooterColumn> = [
    {
        heading: "Conveyancing",
        links: [
            { text: "Get a quote", url: "https://quotes.saillegal.co.uk/legal/quote" },
            { text: "Pricing", url: "/pricing" },
            { text: "Questions", url: "/faq" },
        ],
    },
    {
        heading: "About",
        links: [
            { text: "About us", url: "/about" },
            { text: "Contact us", url: "/contact" },
        ],
    },
    {
        heading: "Legal",
        links: [
            { text: "Privacy policy", url: "/compliance/privacy-policy" },
            { text: "Terms & conditions", url: "/compliance/terms-and-conditions" },
            { text: "Complaints procedure", url: "/compliance/complaints-procedure" },
            { text: "Equality policy", url: "/compliance/equality-policy" },
        ],
    },
];

const FOOTER_MARKS: Array<{ src: string; alt: string }> = [
    { src: "/static/img/prs_logo.png", alt: "Property Redress Scheme" },
    { src: "/static/img/tpo-logo.jpeg", alt: "The Property Ombudsman" },
    { src: "/static/img/tsi-logo.png", alt: "Trading Standards Institute Approved Code" },
];

export const MarketingFooter = (props: { view: TMarketingChrome }): JSX.Element =>
    <footer className={`marketingFooter${isSailLegal(props.view) ? " marketingFooter--sailLegal" : ""}`}>
        <div className="marketingFooter__inner">
            <div className="marketingFooter__grid">
                <div className="marketingFooter__brand">
                    <p className="marketingFooter__wordmark">{isSailLegal(props.view) ? "Sail Legal" : "Sail Homes"}</p>
                    <p className="marketingFooter__blurb">
                        {isSailLegal(props.view)
                            ? "Licensed conveyancers for probate and divorce sales, and everything else that comes with them."
                            : "Estate agency and conveyancing under one roof, for people selling a home they've inherited."}
                    </p>
                    <a className="marketingFooter__tel" href={props.view.phoneUrl}>{props.view.phoneText}</a>
                </div>

                {(isSailLegal(props.view) ? SAIL_LEGAL_FOOTER_COLUMNS : FOOTER_COLUMNS).map((column) =>
                    <div className="marketingFooter__column" key={column.heading}>
                        <h2 className="marketingFooter__columnHeading">{column.heading}</h2>
                        {column.links.map((link) =>
                            <a className="marketingFooter__link" href={link.url} key={link.url}>{link.text}</a>,
                        )}
                    </div>,
                )}
            </div>

            <div className="marketingFooter__marks">
                {isSailLegal(props.view) && (
                    <div className="marketingFooter__secureBadge">
                        <div className="marketingFooter__secureBadgeFrame">
                            <iframe
                                title="Verify Sail Legal's CLC regulation"
                                src="https://cdn.yoshki.com/iframe/54885r.html"
                                scrolling="no"
                            />
                        </div>
                    </div>
                )}
                {(isSailLegal(props.view) ? [] : FOOTER_MARKS).map((mark) =>
                    <img
                        className="marketingFooter__mark"
                        src={mark.src}
                        alt={mark.alt}
                        key={mark.src}
                        height={38}
                        loading="lazy"
                    />,
                )}
            </div>

            {/* Company name, registered number, place of registration and registered office
                are required on a UK company's website by the Company, Limited Liability
                Partnership and Business (Names and Trading Disclosures) Regulations 2015.
                None of it appeared anywhere on the Webflow site, and the entity name we had
                here was wrong — the published terms and privacy policy both state the
                trading entity is PropertyBid Limited, not "Sail Homes Ltd". */}
            <div className="marketingFooter__legal">
                {isSailLegal(props.view)
                    ? <p className="marketingFooter__legalLine">
                        Sail Legal Limited is registered in England and Wales under company
                        number 13307984, and is regulated by the Council for Licensed
                        Conveyancers under licence 14065.
                    </p>
                    : <React.Fragment>
                        <p className="marketingFooter__legalLine">
                            Sail Homes is a trading name of PropertyBid Limited, registered in England
                            and Wales under company number 12428456. Registered office:{" "}
                            {PROPERTYBID_REGISTERED_OFFICE}. VAT number 349323001.
                        </p>
                        <p className="marketingFooter__legalLine">
                            Conveyancing is carried out by Sail Legal Limited, regulated by the Council
                            for Licensed Conveyancers under licence 14065.
                        </p>
                    </React.Fragment>}
                <p className="marketingFooter__legalLine">
                    © {isSailLegal(props.view) ? "Sail Legal" : "Sail Homes"} {new Date().getUTCFullYear()}
                </p>
            </div>
        </div>

        {/* In the footer because all three marketing surfaces render it, so consent is asked
            once from one place rather than wired into each page separately. */}
        <CookieConsent />
    </footer>;
